import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Organization } from '@/features/organizations/api/organization.schemas';

/**
 * OrganizationSelector Component
 * 
 * Dropdown selector for switching between user's available organizations.
 * 
 * Responsibilities:
 * - Display available organizations
 * - Handle organization selection
 * - Show current organization
 * - Display single organization name when only one available
 */

interface OrganizationSelectorProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onOrganizationChange: (organization: Organization) => void;
  loading?: boolean;
}

export function OrganizationSelector({
  organizations,
  selectedOrganization,
  onOrganizationChange,
  loading = false,
}: OrganizationSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // If only one organization, display it without dropdown
  if (organizations.length === 1) {
    const singleOrg = organizations[0];
    return (
      <div className="flex items-center justify-between w-full min-w-0 gap-2">
        <span className="text-sm font-medium truncate flex-1">{singleOrg.name}</span>
        <span className="text-[11px] text-muted-foreground flex-shrink-0">
          {singleOrg.role}
        </span>
      </div>
    );
  }

  // If no organizations, show loading or empty state
  if (organizations.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {loading ? 'Loading organizations...' : 'No organizations'}
        </span>
      </div>
    );
  }

  const handleValueChange = (value: string) => {
    const organization = organizations.find(org => org.id === value);
    if (organization) {
      onOrganizationChange(organization);
    }
    setIsOpen(false);
  };

  return (
    <Select
      value={selectedOrganization?.id || ''}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={loading}
    >
      <SelectTrigger className="w-full justify-between">
        {selectedOrganization ? (
          <div className="flex items-center justify-between w-full min-w-0 gap-2">
            <span className="truncate text-left flex-1">{selectedOrganization.name}</span>
            <span className="text-[11px] text-muted-foreground flex-shrink-0 ml-auto">
              {selectedOrganization.role}
            </span>
          </div>
        ) : (
          <SelectValue placeholder="Select organization" />
        )}
      </SelectTrigger>
      <SelectContent>
        {organizations.map((organization) => (
          <SelectItem key={organization.id} value={organization.id}>
            <div className="flex items-center justify-between w-full min-w-0 gap-2">
              <span className="truncate flex-1">{organization.name}</span>
              <span className="text-[11px] text-muted-foreground flex-shrink-0 ml-auto">
                {organization.role}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}